(function ($) {
  // ---------------------------------------------------------------------------
  // Consent gated Acast episode embeds.
  //
  // Mirrors assets/src/js/lib/video.js and lib/spotify.js for the consent gate,
  // but the player is Acast's own iframe
  // (`https://embed.acast.com/{showId}/{episodeId}`, width 100% / height 190)
  // driven by the documented postMessage command.
  //
  // Acast only accepts the play command once its runtime is listening, and a
  // command sent at mount/load time is dropped. So the player is *prepared* on
  // consent and fired on the click:
  //
  //   1. consent granted → the iframe is mounted immediately behind the facade
  //      and marked `.is-loading`. It is rendered — its `ready` message has to
  //      be able to fire — but transparent and pointer-events:none, so the
  //      thumbnail facade keeps owning the clicks. No playback starts yet.
  //   2. the runtime posts `{context:"player.js", event:"ready"}` (or the
  //      documented `on:load` signal) → `.is-ready` is added and the custom play
  //      disc appears.
  //   3. the visitor activates the play disc → `{eventName:"postmessage:do:play"}`
  //      (no `data`) is posted to the already-ready iframe SYNCHRONOUSLY inside
  //      the click handler, and `.is-loading`/`.is-ready` are swapped for
  //      `.is-playing`, revealing the native player and hiding the facade.
  //
  // If readiness never arrives within READY_TIMEOUT_MS, `.is-fallback` is set
  // instead: the native controls are revealed so the visitor can still start
  // playback manually. Playback is never fired on page load or consent.
  //
  // This module owns `.podcast[data-platform="acast"]` only; Spotify embeds are
  // handled by lib/spotify.js and the two never touch each other's media
  // buttons (each selector is scoped to its own `data-platform`).
  //
  // Focus chain (keyboard):
  //
  //   accept button → Enter → media ("Play episode")   [grant hands focus over]
  //   media         → Enter → iframe → Tab → the player's own controls
  //
  // Pointer activation never moves focus.
  //
  // Accept button / consent cookie: the plugin also has a delegated document
  // handler for `.wpconsent-iframe-accept-button` that saves only the single
  // category named on the button — `{essential, marketing}` here. That object
  // does not cover every entry in `wpconsent.slugs`, so on the next pageview
  // `processBannerDisplay()` reads it as outdated, expires the cookie, brings
  // the privacy banner back and leaves analytics locked even though the visitor
  // just accepted. So this file binds a listener at target phase on OUR gate
  // button only (inside `.podcast__consent`, never the plugin's own
  // placeholders) and:
  //
  //   feature-detect at click time — `window.WPConsent.savePreferences` plus an
  //   array `window.wpconsent.slugs`. Present → `stopPropagation()` first (the
  //   plugin listens at document, in bubble phase, so its partial save never
  //   runs: one write, not two), then hand `savePreferences()` a COMPLETE
  //   preferences object built by `buildStatsOnlyPrefs()`: `{essential: true}`
  //   plus every slug in `wpconsent.slugs` — `marketing` true, `marketing`
  //   false, and each service term resolved through `wpconsent.wpca_services`
  //   (true only when its category is `marketing`, otherwise false, unknown
  //   service → false) — with `marketing: true` guaranteed at the end, because
  //   marketing is the category this gate accepts.
  //
  //   Absent → do NOT stop propagation: the plugin's stock handler saves its
  //   partial object, which degrades to today's behaviour. This file never
  //   writes a consent cookie or calls `wp_set_consent` itself — every write
  //   stays inside the plugin's `savePreferences()` (cookie write, banner hide,
  //   script/iframe unlock, `wp_set_consent`, `wpconsent_consent_saved`), so
  //   the consent event chain that drives `evaluate()` below is unchanged.
  // ---------------------------------------------------------------------------
  var CONSENT_CATEGORY = "marketing";
  var IFRAME_HEIGHT = "190";
  var IFRAME_ALLOW =
    "autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture";
  // Official Acast Embed Player API origin and command. The command carries no
  // `data` field: Acast treats `{}` as an explicit (empty) episode payload.
  var ACAST_EMBED_ORIGIN = "https://embed.acast.com";
  var ACAST_PLAY_COMMAND = JSON.stringify({
    eventName: "postmessage:do:play",
  });
  // Bounded wait for the runtime's ready message before handing the box to the
  // native controls.
  var READY_TIMEOUT_MS = 6000;
  // Per-embed readiness timers (embed element → timeout id).
  var readyTimers = new WeakMap();

  function hasConsent() {
    if (typeof window.wp_has_consent !== "function") {
      return false;
    }

    try {
      return !!window.wp_has_consent(CONSENT_CATEGORY);
    } catch (e) {
      return false;
    }
  }

  // Shapes the object the plugin's savePreferences() writes — it never writes
  // anything itself. The gate accepts "marketing", so the recorded choice has
  // to name every slug in wpconsent.slugs (the plugin expires a cookie whose
  // keys don't, and re-shows the banner on the next pageview). Service terms
  // are resolved via wpca_services because manual_toggle_services makes the
  // service key — not the category — decide whether that service unlocks, so
  // marketing services must be explicitly true for analytics to run later.
  function buildStatsOnlyPrefs() {
    var prefs = { essential: true };
    var slugs = window.wpconsent.slugs;
    var services = Array.isArray(window.wpconsent.wpca_services)
      ? window.wpconsent.wpca_services
      : [];

    slugs.forEach(function (slug) {
      if (slug === "essential") {
        return;
      }

      if (slug === CONSENT_CATEGORY) {
        prefs[slug] = true;
        return;
      }

      if (slug === "marketing") {
        prefs[slug] = false;
        return;
      }

      // Anything else is a service term: look up its category and only unlock
      // the services that belong to marketing (unknown service → false).
      var category = null;

      for (var i = 0; i < services.length; i++) {
        if (services[i] && services[i].slug === slug) {
          category = services[i].category;
          break;
        }
      }

      prefs[slug] = category === CONSENT_CATEGORY;
    });

    // The category this button accepts, whatever the slug list contained.
    prefs[CONSENT_CATEGORY] = true;

    return prefs;
  }

  // Target-phase listener on our own gate button. The plugin's partial-save
  // handler sits at document (bubble phase), so stopping propagation here
  // keeps it from running after us — no double write — while a missing API
  // falls through untouched to that stock handler. Detection runs at click
  // time, so a plugin script that loads after this bundle still counts.
  function bindGateAccept(button) {
    button.addEventListener("click", function (e) {
      var api = window.WPConsent;
      var config = window.wpconsent;

      if (
        !api ||
        typeof api.savePreferences !== "function" ||
        !config ||
        !Array.isArray(config.slugs)
      ) {
        // No API: let the plugin's own document handler run (partial save =
        // current behaviour). We never write a consent cookie ourselves.
        return;
      }

      e.stopPropagation();
      api.savePreferences(buildStatsOnlyPrefs());
    });
  }

  function acastEmbeds() {
    return $('.podcast[data-platform="acast"]');
  }

  // The `.podcast__frame` host <div>. It stays in place for the lifetime of the
  // embed; the Acast iframe is mounted inside it on consent and removed on
  // revoke.
  function getHost(embed) {
    return $(embed).find(".podcast__frame")[0] || null;
  }

  function ensureHost(embed) {
    var host = getHost(embed);

    if (host) {
      return host;
    }

    host = document.createElement("div");
    host.className = "podcast__frame";
    embed.appendChild(host);

    return host;
  }

  // Page HTML is cached, so a page rendered before the facade became a <button>
  // may still carry a plain <div> as its play control. Mirror the button
  // semantics in place. Consent state itself is never touched here.
  function upgradeMedia(embed) {
    var media = $(embed).find(".podcast__media")[0];

    if (!media || media.tagName === "BUTTON") {
      return;
    }

    media.setAttribute("role", "button");
    media.setAttribute("tabindex", "0");
    media.setAttribute("aria-label", "Play episode");
  }

  // Activating a control hides it (play swaps the facade for the player, a
  // grant swaps the consent layer for the play disc, a revoke swaps back),
  // which drops focus to <body> and loses the visitor's place: the next Tab
  // restarts at the top of the document. So after keyboard activation the
  // focus moves to whichever surface is visible now.
  //
  // Pointer activation is deliberately left alone — it only changes where Tab
  // would continue from, and moving focus after a click can flash a ring the
  // visitor never asked for. `:focus-visible` distinguishes the two for native
  // buttons and for the legacy <div> path alike. Read it before the state swap:
  // a hidden element no longer reports :focus-visible.
  function wasKeyboard(el) {
    try {
      return el.matches(":focus-visible");
    } catch (e) {
      // No :focus-visible support: prefer the accessible behaviour.
      return true;
    }
  }

  function focusOrFail(el) {
    // focus() on a control that ended up display:none is a silent no-op.
    if (el && typeof el.focus === "function") {
      el.focus();
    }
  }

  function focusPlayer(embed) {
    focusOrFail(embed.querySelector("iframe"));
  }

  function clearReadyTimer(embed) {
    var timer = readyTimers.get(embed);

    if (timer) {
      window.clearTimeout(timer);
      readyTimers.delete(embed);
    }
  }

  function setReadyTimer(embed) {
    clearReadyTimer(embed);

    var timer = window.setTimeout(function () {
      readyTimers.delete(embed);
      fallbackToNative(embed);
    }, READY_TIMEOUT_MS);

    readyTimers.set(embed, timer);
  }

  // Mounts the Acast iframe behind the facade. Called once marketing consent is
  // granted (never before), so the runtime can announce readiness ahead of the
  // click. `.is-loading` keeps it transparent and non-interactive under the
  // thumbnail; the frame is still rendered so its ready message can fire.
  function mountPlayer(embed) {
    // Already mounted: keep the existing frame (and its pending timer).
    if (embed.querySelector("iframe")) {
      return;
    }

    var src = embed.getAttribute("data-src") || "";

    if (!src) {
      return;
    }

    var host = ensureHost(embed);

    if (!host) {
      return;
    }

    var frame = document.createElement("iframe");
    frame.setAttribute("src", src);
    frame.setAttribute("title", "Acast player");
    frame.setAttribute("width", "100%");
    frame.setAttribute("height", IFRAME_HEIGHT);
    frame.setAttribute("frameborder", "0");
    frame.setAttribute("allow", IFRAME_ALLOW);
    frame.setAttribute("allowfullscreen", "");
    frame.setAttribute("loading", "lazy");

    host.appendChild(frame);

    $(embed)
      .addClass("is-loading")
      .removeClass("is-playing")
      .removeClass("is-fallback");

    // Bounded wait for readiness: if the runtime never announces itself the
    // native controls are revealed instead.
    setReadyTimer(embed);
  }

  // The runtime is listening: enable the custom play and stop the fallback wait.
  function onPlayerReady(embed) {
    clearReadyTimer(embed);

    if (!hasConsent()) {
      return;
    }

    var $embed = $(embed);

    if ($embed.hasClass("is-playing") || $embed.hasClass("is-fallback")) {
      return;
    }

    $embed.addClass("is-ready");
  }

  // Readiness never arrived (or the player failed): hand the box to Acast's own
  // controls so the visitor can still start playback manually.
  function fallbackToNative(embed) {
    if (!hasConsent()) {
      return;
    }

    // No frame to fall back to: leave the facade as it is.
    if (!embed.querySelector("iframe")) {
      return;
    }

    var $embed = $(embed);

    if ($embed.hasClass("is-playing")) {
      return;
    }

    $embed.removeClass("is-ready").addClass("is-fallback");
  }

  // Sends the documented play command to the already-ready iframe. Runs
  // synchronously inside the click/keyboard gesture, so the browser treats it as
  // user-initiated playback. The native player is revealed either way; if the
  // command is rejected, the visitor can still start playback from its own
  // controls.
  function play(embed) {
    var frame = embed.querySelector("iframe");

    // Nothing mounted (mount failed): keep the facade rather than show an empty
    // box.
    if (!frame) {
      return;
    }

    var win = frame.contentWindow;

    if (win) {
      try {
        win.postMessage(ACAST_PLAY_COMMAND, ACAST_EMBED_ORIGIN);
      } catch (e) {
        // Cross-origin/blocked: the native controls still work.
      }
    }

    $(embed)
      .removeClass("is-loading")
      .removeClass("is-ready")
      .addClass("is-playing");
  }

  function stop(embed) {
    clearReadyTimer(embed);

    // Removing the iframe tears the player down; the empty `.podcast__frame`
    // host stays so a later consent cycle can mount again.
    var frames = embed.querySelectorAll("iframe");

    Array.prototype.forEach.call(frames, function (frame) {
      if (frame.parentNode) {
        frame.parentNode.removeChild(frame);
      }
    });

    ensureHost(embed);

    $(embed)
      .removeClass("is-playing")
      .removeClass("is-ready")
      .removeClass("is-loading")
      .removeClass("is-fallback");
  }

  // Single source of truth for the facade states. The consent decision is never
  // written into the cached HTML: `.is-gated` is added (or removed) from here
  // only, which is what keeps an already-consented page load free of the accept
  // button while a missing consent API — which reads as "no consent" — still
  // ends with the button on screen.
  function evaluate() {
    var granted = hasConsent();
    var active = document.activeElement;
    var refocus = null;

    // The consent layer is what holds focus when a grant lands (clicking or
    // keyboard-activating its accept button is how consent gets granted), and
    // hiding it would strand focus on <body>.
    if (
      granted &&
      active &&
      active.closest &&
      active.closest(".podcast__consent")
    ) {
      refocus =
        active.closest('.podcast[data-platform="acast"]') ||
        active.closest(".podcast");
    }

    acastEmbeds().toggleClass("is-gated", !granted);

    if (granted) {
      // Prepare the player behind the facade: mount it and wait for the ready
      // message (falling back to the native controls on timeout). The custom
      // play is only enabled once readiness is known.
      acastEmbeds().each(function () {
        mountPlayer(this);
      });
    }

    if (refocus) {
      // The play disc has just appeared where the accept button was — the next
      // step of the same two-step flow.
      focusOrFail(refocus.querySelector(".podcast__media"));
    }
  }

  function revoke() {
    var active = document.activeElement;
    var refocus = null;

    // Keyboard play leaves focus inside the iframe; the revoke tears that frame
    // down, so pick the surface to hand focus to before anything hides.
    acastEmbeds().each(function () {
      var frame = this.querySelector("iframe");

      if (
        frame &&
        active &&
        (active === frame || frame.contains(active)) &&
        wasKeyboard(active)
      ) {
        refocus = this;
      }
    });

    acastEmbeds()
      .removeClass("is-ready")
      .removeClass("is-loading")
      .removeClass("is-fallback")
      .addClass("is-gated")
      .each(function () {
        stop(this);
      });

    if (refocus) {
      // The player is gone; the consent layer is the live surface again.
      focusOrFail(
        refocus.querySelector(".podcast__consent button") ||
          refocus.querySelector(".podcast__media"),
      );
    }
  }

  // Parses an incoming message payload. Acast may post a JSON string or a
  // structured-cloned object, so both are accepted.
  function acastMessageData(raw) {
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }

    return raw && typeof raw === "object" ? raw : null;
  }

  // Whether a parsed message is the player runtime announcing readiness.
  //
  // Acast posts `{context:"player.js", event:"ready", ...}` once its player is
  // able to accept commands; the documented `on:load` signal is accepted as an
  // equivalent readiness marker. Nothing is ever sent before readiness.
  function isAcastReady(data) {
    if (!data || typeof data !== "object") {
      return false;
    }

    if (data.context === "player.js" && data.event === "ready") {
      return true;
    }

    return data.event === "on:load";
  }

  // Single window message listener for the Acast player runtime. Only a message
  // from the embed origin, sent by one of our mounted frames, and announcing
  // readiness is acted on; the play command is sent later, from the click.
  window.addEventListener("message", function (e) {
    if (e.origin !== ACAST_EMBED_ORIGIN || !e.source) {
      return;
    }

    if (!isAcastReady(acastMessageData(e.data))) {
      return;
    }

    // Match the message back to the frame that sent it.
    acastEmbeds().each(function () {
      var frame = this.querySelector("iframe");

      if (frame && frame.contentWindow === e.source) {
        onPlayerReady(this);
      }
    });
  });

  // Delegated so dynamically inserted sections keep working.
  document.addEventListener("click", function (e) {
    var target = e.target;
    if (!target || !target.closest) {
      return;
    }

    // The consent layer is a sibling of the media control, so its accept button
    // never resolves to `.podcast__media`: the plugin owns that click and this
    // handler stays out of its way (no second handler, no play).
    var media = target.closest(".podcast__media");
    if (!media) {
      return;
    }

    // Scoped to Acast: a Spotify embed's media button must not reach here.
    var embed = media.closest('.podcast[data-platform="acast"]');
    var $embed = $(embed);

    // The custom play only exists once the player is ready; the fallback state
    // hides it entirely and lets the native controls drive playback.
    if (
      !embed ||
      !$embed.hasClass("is-ready") ||
      $embed.hasClass("is-playing") ||
      $embed.hasClass("is-fallback")
    ) {
      return;
    }

    var keyboardPlay = wasKeyboard(media);
    play(embed);

    if (keyboardPlay) {
      // The facade is display:none now, so focusing it would silently do
      // nothing and focus would fall to <body>. The player is the live surface.
      focusPlayer(embed);
    }
  });

  // Enter/Space for facade markup that predates the <button> element. Native
  // buttons are skipped so the browser's own activation is never doubled.
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") {
      return;
    }

    var media =
      e.target && e.target.closest
        ? e.target.closest(".podcast__media")
        : null;
    if (!media || media.tagName === "BUTTON") {
      return;
    }

    if (!media.closest('.podcast[data-platform="acast"]')) {
      return;
    }

    e.preventDefault();
    media.click();
  });

  document.addEventListener("wp_listen_for_consent_change", function (e) {
    if (!e.detail || !e.detail[CONSENT_CATEGORY]) {
      return;
    }

    if (e.detail[CONSENT_CATEGORY] === "allow") {
      evaluate();
    } else if (e.detail[CONSENT_CATEGORY] === "deny") {
      revoke();
    }
  });

  document.addEventListener("wp_consent_type_defined", function () {
    evaluate();
  });

  $(() => {
    acastEmbeds().each(function () {
      upgradeMedia(this);

      // Our gate's accept button only — scoped by the selector, so the
      // plugin's placeholder buttons elsewhere on the page keep their own
      // handler untouched.
      var accept = this.querySelector(
        ".podcast__consent .wpconsent-iframe-accept-button",
      );

      if (accept) {
        bindGateAccept(accept);
      }
    });

    evaluate();

    // Safety net for a consent script that loads after the app bundle.
    if (typeof window.wp_has_consent !== "function") {
      setTimeout(evaluate, 2000);
    }
  });
})(jQuery);
